import { AssigneeSelect } from "@/components/AssigneeSelect";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { PrioritySelect } from "./PrioritySelect";
import { StatusSelect } from "@/components/StatusSelect";
import { cn } from "@/lib/utils";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";

export function TaskListItem({
  task,
  organizationSlug,
  className,
  onTaskUpdate,
  onSelectionChange,
  isSelected,
  isDragging,
  selectedCount,
  isSelectedButNotDragging,
  columnConfig = {
    checkbox: true,
    key: true,
    summary: true,
    status: true,
    assignee: true,
    priority: true,
    dueDate: true,
    actions: true
  },
  ...props
}) {
  const navigate = useNavigate();

  const handleTaskChange = (updatedTask) => {
    const mappedTask = {
      ...task,
      ...updatedTask,
      id: updatedTask._id || task.id,
      status: updatedTask.status || task.status,
      assignee: updatedTask.assignee,
      priority: updatedTask.priority || task.priority,
      order: task.order
    };

    if (onTaskUpdate) {
      onTaskUpdate(mappedTask);
    }
  };

  const handleRowClick = (e) => {
    if (e.target.closest('.task-action')) {
      e.stopPropagation();
      return;
    }
    navigate(`/${organizationSlug}/plan/${task.key}`);
  };

  const handleCheckboxChange = (checked) => {
    if (onSelectionChange) {
      onSelectionChange(task.id, checked);
    }
  };

  // Build grid columns dynamically based on config
  const getGridTemplateColumns = () => {
    const columns = [];
    if (columnConfig.checkbox) columns.push('auto');
    if (columnConfig.key) columns.push('80px');
    if (columnConfig.summary) columns.push('1fr');
    if (columnConfig.status) columns.push('140px');
    if (columnConfig.assignee) columns.push('160px');
    if (columnConfig.priority) columns.push('100px');
    if (columnConfig.dueDate) columns.push('120px');
    if (columnConfig.actions) columns.push('48px');
    return columns.join(' ');
  };

  return (
    <div
      onClick={handleRowClick}
      className={cn(
        "grid gap-4 items-center p-3 rounded-lg dark:bg-muted/20 dark:hover:bg-muted/60 bg-muted/40 hover:bg-muted/80 h-16 cursor-pointer transition-colors",
        isSelected && !isSelectedButNotDragging && "dark:bg-muted/60 bg-muted/80 ring-2 ring-violet-500 ring-offset-0 ring-offset-background",
        isDragging && selectedCount > 1 && "relative",
        isSelectedButNotDragging && "opacity-30",
        className
      )}
      style={{ gridTemplateColumns: getGridTemplateColumns() }}
      {...props}
    >
      {isDragging && selectedCount > 1 && (
        <div className="absolute -left-3 top-1/2 -translate-y-1/2 bg-violet-500 text-white text-sm rounded-full w-7 h-7 flex items-center justify-center font-medium shadow-md">
          {selectedCount}
        </div>
      )}

      {columnConfig.checkbox && (
        <div className="task-action flex items-center justify-center w-8 h-8 cursor-default group" onClick={(e) => {
          e.stopPropagation();
          handleCheckboxChange(!isSelected);
        }}>
          <Checkbox
            checked={isSelected}
            onCheckedChange={handleCheckboxChange}
            className="cursor-default group-hover:ring-2 group-hover:ring-primary data-[state=checked]:ring-2 data-[state=checked]:ring-primary"
          />
        </div>
      )}

      {columnConfig.key && (
        <div className="text-sm font-medium text-muted-foreground">{task.key}</div>
      )}

      {columnConfig.summary && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="text-sm truncate min-w-[100px]">
                {task.summary}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{task.summary}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {columnConfig.status && (
        <div className="task-action">
          <StatusSelect
            task={task}
            onTaskChange={handleTaskChange}
          />
        </div>
      )}

      {columnConfig.assignee && (
        <div className="task-action">
          <AssigneeSelect
            task={task}
            organizationSlug={organizationSlug}
            onTaskChange={handleTaskChange}
            showName
          />
        </div>
      )}

      {columnConfig.priority && (
        <div className="flex task-action">
          <PrioritySelect
            task={task}
            onTaskChange={handleTaskChange}
          />
        </div>
      )}

      {columnConfig.dueDate && (
        <div>
          <span className="text-muted-foreground">
            {task.dueDate ? format(new Date(task.dueDate), "MMM d, yyyy") : 'No Due Date'}
          </span>
        </div>
      )}

      {columnConfig.actions && (
        <div className="task-action">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuItem>Copy Link</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
  );
}
